import { cn } from "@/lib/utils";
import { Sparkles } from "lucide-react";

interface FieldsLoadingStateProps {
    /** Tone of the loading state. Fetching is fast; extracting can take 30s+. */
    variant?: "fetching" | "extracting";
}

// Neutral product verbs only. "Reading…" anthropomorphizes the system; the
// status copy reads as background work, not as the AI doing something to the
// user's file. See memory: feedback_ux_copy_neutral_verbs.
const EXTRACTING_COPY = {
    title: "Extracting fields",
    subtitle: "Processing in the background",
} as const;

const PLACEHOLDER_FIELDS = [
    { name: "w-32", value: "w-24" },
    { name: "w-40", value: "w-32" },
    { name: "w-28", value: "w-36" },
    { name: "w-36", value: "w-20" },
    { name: "w-24", value: "w-28" },
] as const;

const SHIMMER_BG =
    "bg-[linear-gradient(110deg,oklch(0.965_0.008_266)_0%,oklch(0.965_0.008_266)_38%,oklch(0.6756_0.1849_42.89/0.18)_50%,oklch(0.965_0.008_266)_62%,oklch(0.965_0.008_266)_100%)] " +
    "dark:bg-[linear-gradient(110deg,oklch(0.269_0.01_266)_0%,oklch(0.269_0.01_266)_38%,oklch(0.6756_0.1849_42.89/0.22)_50%,oklch(0.269_0.01_266)_62%,oklch(0.269_0.01_266)_100%)] " +
    "bg-[length:220%_100%]";

const SHIMMER_ANIM = "animate-[fields-shimmer_2.4s_ease-in-out_infinite]";

export function FieldsLoadingState({
    variant = "extracting",
}: FieldsLoadingStateProps) {
    return (
        <div
            role="status"
            aria-live="polite"
            aria-atomic="true"
            data-testid={
                variant === "extracting"
                    ? "fields-processing"
                    : "fields-loading"
            }
            className="space-y-3"
        >
            {/* Banner only on `extracting`. `fetching` is silent so the
                toolbar is the user's anchor while the first response lands. */}
            {variant === "extracting" && (
                <div className="border-border/70 bg-card relative flex items-center gap-3 overflow-hidden rounded-lg border px-3.5 py-2.5">
                    <div className="bg-copper/10 text-copper relative flex size-7 shrink-0 items-center justify-center rounded-md">
                        <Sparkles
                            className="size-3.5 [animation:fields-spark-pulse_1.8s_ease-in-out_infinite]"
                            strokeWidth={2.2}
                        />
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col leading-tight">
                        <span className="text-foreground text-[13px] font-medium tracking-[-0.005em]">
                            {EXTRACTING_COPY.title}
                            <DotProgression />
                        </span>
                        <span className="text-muted-foreground/80 text-[11px]">
                            {EXTRACTING_COPY.subtitle}
                        </span>
                    </div>
                    {/* Hairline copper sweep across the banner */}
                    <div
                        aria-hidden
                        className={cn(
                            "pointer-events-none absolute inset-x-0 -bottom-px h-px",
                            "bg-[linear-gradient(90deg,transparent_0%,oklch(0.6756_0.1849_42.89/0.55)_50%,transparent_100%)]",
                            "bg-[length:200%_100%]",
                            "animate-[fields-shimmer_2.4s_ease-in-out_infinite]"
                        )}
                    />
                </div>
            )}

            {/* Field rows; staggered fade-in mirrors the real layout */}
            <div className="space-y-1.5">
                {PLACEHOLDER_FIELDS.map((row, i) => (
                    <div
                        key={i}
                        aria-hidden
                        className="flex min-h-[32px] [animation:fields-row-in_360ms_ease-out_forwards] items-center gap-2 px-1 py-1 opacity-0"
                        style={{ animationDelay: `${120 + i * 70}ms` }}
                    >
                        {/* Name */}
                        <div className="max-w-[40%] min-w-0 shrink-0">
                            <div
                                className={cn(
                                    "h-3 rounded-sm",
                                    "bg-muted",
                                    row.name
                                )}
                            />
                        </div>
                        {/* Value (with shimmer) + page chip */}
                        <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5">
                            <div
                                className={cn(
                                    "h-3 rounded-sm",
                                    SHIMMER_BG,
                                    SHIMMER_ANIM,
                                    row.value
                                )}
                                style={{
                                    animationDelay: `${140 + i * 90}ms`,
                                }}
                            />
                            <div
                                className={cn(
                                    "h-4 w-7 rounded-full border",
                                    "border-border/70 bg-muted/70"
                                )}
                            />
                        </div>
                    </div>
                ))}
            </div>

            {variant === "extracting" && (
                <span className="sr-only">
                    Field extraction in progress. This usually takes a few
                    seconds.
                </span>
            )}
        </div>
    );
}

/** Three softly-pulsing dots that succeed each other; calmer than a spinner. */
function DotProgression() {
    return (
        <span className="ml-0.5 inline-flex items-baseline gap-[2px] align-baseline">
            {[0, 1, 2].map((i) => (
                <span
                    key={i}
                    aria-hidden
                    className="text-muted-foreground inline-block [animation:fields-spark-pulse_1.4s_ease-in-out_infinite]"
                    style={{ animationDelay: `${i * 180}ms` }}
                >
                    .
                </span>
            ))}
        </span>
    );
}
